/**
 * Money is always stored as an integer number of minor units (cents, kuruş)
 * plus an ISO 4217 code. Floats never enter storage or aggregation, so
 * totals and averages cannot drift. Number.MAX_SAFE_INTEGER minor units is
 * ~90 trillion dollars, far beyond any household budget.
 */
export type CurrencyCode = string;

export interface Money {
  minor: number;
  currency: CurrencyCode;
}

const exponentCache = new Map<CurrencyCode, number>();

/** Number of minor-unit digits for a currency (USD 2, JPY 0, KWD 3). */
export function currencyExponent(currency: CurrencyCode): number {
  let exp = exponentCache.get(currency);
  if (exp === undefined) {
    exp = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
    exponentCache.set(currency, exp);
  }
  return exp;
}

export function toMinor(major: number, currency: CurrencyCode): number {
  const factor = 10 ** currencyExponent(currency);
  // Round via string to avoid 1.005 * 100 = 100.49999 style errors.
  return Math.round(Number(`${major}e${Math.log10(factor)}`));
}

export function toMajor(minor: number, currency: CurrencyCode): number {
  return minor / 10 ** currencyExponent(currency);
}

export function isValidCurrency(code: string): boolean {
  if (!/^[A-Z]{3}$/.test(code)) return false;
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: code });
    return true;
  } catch {
    return false;
  }
}
